import { Prefix, useGenerateSignedUrlMutation } from '../graphql/types';

const IMAGE_MAX_SIZE_MB = 5;
const VIDEO_MAX_SIZE_MB = 25;
const VIDEO_MAX_BITRATE = 3_000_000; // 3 Mbps

const imageErrors = {
  NO_FILE: 'You must have a file to upload',
  GENERIC_ERROR: 'Failed to upload image. Please try again later',
  TOO_BIG: `This file is too large to send. We support up to ${IMAGE_MAX_SIZE_MB}MB.`,
  UNSUPPORTED_FILE_TYPE: 'This file is not supported. Try a jpg, png, or webp.',
};
const videoErrors = {
  GENERIC_ERROR: 'Failed to upload video. Please try again later',
  NO_FILE: 'You must have a file to upload',
  SIZE_TOO_BIG: `This file is too large to send. We support up to ${VIDEO_MAX_SIZE_MB}MB.`,
  BITRATE_TOO_BIG: `This video is too high quality to send. We support up to ${VIDEO_MAX_BITRATE / 1000000}Mbps.`,
  UNSUPPORTED_FILE_TYPE: 'This file is not supported. Try a mp4.',
};

function isImageTooLarge(file: File) {
  return file.size / 1000000 > IMAGE_MAX_SIZE_MB;
}

function isVideoTooLarge(file: File) {
  return file.size / 1000000 > VIDEO_MAX_SIZE_MB;
}

function validateImage(file?: File) {
  if (!file) {
    throw new Error(imageErrors.NO_FILE);
  }

  // check type
  if (!file.type.match(/jpeg|png|webp/gi)) {
    throw new Error(imageErrors.UNSUPPORTED_FILE_TYPE);
  }
  // check size
  if (isImageTooLarge(file)) {
    throw new Error(imageErrors.TOO_BIG);
  }
}

const validateVideo = async (file?: File) => {
  if (!file) {
    throw new Error(videoErrors.NO_FILE);
  }

  if (!file.type.match(/mp4/gi)) {
    throw new Error(videoErrors.UNSUPPORTED_FILE_TYPE);
  }
  if (isVideoTooLarge(file)) {
    throw new Error(videoErrors.SIZE_TOO_BIG);
  }

  const { bitrate } = await getVideoData(file);

  if (bitrate > VIDEO_MAX_BITRATE) {
    throw new Error(videoErrors.BITRATE_TOO_BIG);
  }
};

function getImageData(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = document.createElement('img');
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      URL.revokeObjectURL(objUrl);
      img.remove();
    };
    img.src = objUrl;
  });
}

const getVideoData = (
  file: File,
): Promise<{
  duration: number;
  bitrate: number;
}> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const objUrl = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        // Approximate bitrate calculation: file size (in bits) divided by duration (in seconds).
        // This does not account for container overhead or metadata.
        bitrate: (8 * file.size) / video.duration,
      });
      URL.revokeObjectURL(objUrl);
      video.remove();
    };
    video.src = objUrl;
  });
};

export function useUploadFile() {
  const [generateSignedUrl] = useGenerateSignedUrlMutation();

  const S3Upload = (signedUrl: string, file: File) =>
    fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

  async function uploadImage(options: {
    file: File;
    prefix?: Prefix;
    urlWithDimensions?: boolean;
  }): Promise<{ url: string; key: string }> {
    const { file, prefix = Prefix.Uploads, urlWithDimensions = true } = options;
    validateImage(file);

    const { width, height } = await getImageData(file);

    // need to generate URL where we will upload file to
    const { data: signedUrlData } = await generateSignedUrl({
      variables: {
        input: {
          prefix,
          contentType: file.type,
        },
      },
    });

    if (!signedUrlData) {
      throw new Error(imageErrors.GENERIC_ERROR);
    }

    const { signedUrl, photo, key } = signedUrlData.generateSignedUrl;

    // finally upload image
    await S3Upload(signedUrl, file);

    // Build URL for image that was just uploaded
    const url = urlWithDimensions
      ? `${photo.basePath}/${
          photo.id
        }/${width}x${height}.${file.type.replace('image/', '')}`
      : `${photo.basePath}/${photo.id}`;

    return { url, key };
  }

  async function uploadVideo(
    file: File,
    prefix: Prefix,
  ): Promise<{ url: string; key: string }> {
    await validateVideo(file);

    // need to generate URL where we will upload file to
    const { data: signedUrlData } = await generateSignedUrl({
      variables: {
        input: {
          prefix,
          contentType: file.type,
        },
      },
    });

    if (!signedUrlData) {
      throw new Error(videoErrors.GENERIC_ERROR);
    }

    const { signedUrl, photo, key } = signedUrlData.generateSignedUrl;

    await S3Upload(signedUrl, file);

    const url = `${photo.basePath}/${photo.id}`;
    return { url, key };
  }

  return {
    uploadImage,
    uploadVideo,
    isFileTooLarge: isImageTooLarge,
    getImageData,
    validateImage,
  };
}
